import { useEffect, useRef, useState } from "react";
import { MailCheck } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { ffTrack } from "@/lib/tracking";

export default function OtpVerify({ email, initialCooldown = 60, expiresIn = 600, onVerified, onBack }) {
  const [digits, setDigits] = useState(Array(6).fill(""));
  const [cooldown, setCooldown] = useState(initialCooldown);
  const [busy, setBusy] = useState(false);
  const [expiresAt, setExpiresAt] = useState(Date.now() + expiresIn * 1000);
  const [now, setNow] = useState(Date.now());
  const refs = useRef([]);
  useEffect(() => { const t = setInterval(() => setNow(Date.now()), 1000); return () => clearInterval(t); }, []);
  const left = Math.max(0, Math.round((expiresAt - now) / 1000));

  useEffect(() => {
    if (cooldown <= 0) return;
    const t = setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);
  useEffect(() => { refs.current[0]?.focus(); }, []);

  const setAt = (i, v) => {
    const clean = v.replace(/\D/g, "");
    if (clean.length > 1) {
      const next = clean.slice(0, 6).split("");
      setDigits([...next, ...Array(6 - next.length).fill("")]);
      refs.current[Math.min(next.length, 5)]?.focus();
      return;
    }
    const next = [...digits]; next[i] = clean; setDigits(next);
    if (clean && i < 5) refs.current[i + 1]?.focus();
  };
  const onKey = (i, e) => { if (e.key === "Backspace" && !digits[i] && i > 0) refs.current[i - 1]?.focus(); };

  const verify = async (e) => {
    e.preventDefault();
    const otp = digits.join("");
    if (otp.length !== 6) return toast.error("Enter the 6-digit code");
    setBusy(true);
    try {
      const { data } = await api.post("/auth/verify-otp", { email, otp });
      ffTrack("CompleteRegistration", { status: true });
      onVerified(data);
    } catch (err) { toast.error(apiError(err)); setDigits(Array(6).fill("")); refs.current[0]?.focus(); }
    setBusy(false);
  };
  const resend = async () => {
    try {
      const { data } = await api.post("/auth/resend-otp", { email });
      setCooldown(data.resend_in || 60); setExpiresAt(Date.now() + (data.expires_in || 600) * 1000); setDigits(Array(6).fill(""));
      toast.success("A new code is on its way. Your previous code no longer works");
    } catch (err) { toast.error(apiError(err)); }
  };

  return (
    <form onSubmit={verify} className="mt-6 fade-in" data-testid="otp-step">
      <div className="h-14 w-14 rounded-2xl bg-primary/15 grid place-items-center mx-auto"><MailCheck className="h-7 w-7 text-primary" /></div>
      <h2 className="text-xl font-display font-bold text-center mt-4">Verify your email</h2>
      <p className="text-sm text-muted-foreground text-center mt-1">We sent a 6-digit code to <span className="text-foreground font-medium" data-testid="otp-email">{email}</span>. </p>
      <p className={`text-xs text-center mt-2 ${left ? "text-muted-foreground" : "text-destructive"}`} data-testid="otp-expiry-timer">
        {left ? `Code expires in ${Math.floor(left / 60)}:${String(left % 60).padStart(2, "0")}` : "Code expired. Tap Resend code to get a new one"}</p>
      <div className="flex justify-center gap-2 mt-6">
        {digits.map((d, i) => (
          <input key={i} ref={(el) => (refs.current[i] = el)} value={d} inputMode="numeric" autoComplete="one-time-code"
            maxLength={6} onChange={(e) => setAt(i, e.target.value)} onKeyDown={(e) => onKey(i, e)} data-testid={`otp-digit-${i}`}
            className="h-12 w-11 text-center text-xl font-bold bg-background border border-border rounded-xl outline-none focus:border-primary transition-colors" />
        ))}
      </div>
      <button disabled={busy || !left} data-testid="otp-verify-btn" className="w-full mt-6 px-6 py-3 rounded-xl bg-primary text-primary-foreground font-semibold hover:opacity-90 disabled:opacity-50">
        {busy ? "Verifying…" : "Verify & Create Account"}
      </button>
      <div className="flex items-center justify-between mt-4 text-sm">
        <button type="button" onClick={onBack} className="text-muted-foreground hover:text-foreground" data-testid="otp-back-btn">Change email</button>
        <button type="button" onClick={resend} disabled={cooldown > 0} data-testid="otp-resend-btn" className="text-primary disabled:text-muted-foreground">
          {cooldown > 0 ? `Resend in ${cooldown}s` : "Resend code"}
        </button>
      </div>
    </form>
  );
}
